import React, { useState, useEffect, CSSProperties } from 'react';
import { VERSION } from '../../../../common/constants/api';
import { AppConfig } from '../../../../Config';
import { authenticatedFetch } from '../../../../common/utils/authFetch';
import { PDFViewer, PdfViewerProps } from './PDFViewer';
import VideoViewer from './VideoViewer';
import { Icon } from 'semantic-ui-react';
import { FileTypes } from '../../../../common/constants/filetypes';
import { getCurrentOrganizationId } from '../../../../common/selectors/auth';
import { useSelector } from 'react-redux';
import { getYouTubeEmbedUrl } from 'common/utils/resourceLinks';

export interface FileViewerProps {
  fileId: string;
  style?: CSSProperties;
  isFullScreen?: boolean;
  guestToken?: string;
  viewMode?: 'scale' | 'fitWidth' | 'fullPage';
  scale?: number;
  onViewModeChange?: (mode: 'scale' | 'fitWidth' | 'fullPage') => void;
  onScaleChange?: (scale: number) => void;
  pdfViewerComponent?: React.ComponentType<PdfViewerProps>;
}

const FileViewer: React.FC<FileViewerProps> = ({
  guestToken,
  fileId,
  style,
  isFullScreen,
  viewMode,
  scale,
  onViewModeChange,
  onScaleChange,
  pdfViewerComponent,
}) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const [resourceUrl, setResourceUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const organizationId = useSelector(getCurrentOrganizationId);

  useEffect(() => {
    // Reset state when fileId changes
    setFileUrl(null);
    setBlob(null);
    setFileType(null);
    setResourceUrl(null);
    setError(null);

    const fetchFile = async () => {
      try {
        // Guest token uses a different scheme (Token <opaque>) and is not a Cognito session,
        // so it bypasses authenticatedFetch and stays on raw fetch. Cognito sessions go
        // through authenticatedFetch for refresh + signOut on auth failure.
        const headers: HeadersInit = {};
        if (guestToken) {
          headers.Authorization = `Token ${guestToken}`;
        }
        if (organizationId) {
          headers['X-NULOGIC-ORGANIZATION-ID'] = organizationId.toString();
        }

        const doFetch = (url: string) =>
          guestToken
            ? fetch(url, { method: 'GET', headers })
            : authenticatedFetch(url, { method: 'GET', headers });

        // First, try to fetch file metadata to get filename or link information.
        let fallbackFilename: string | null = null;
        let linkUrl: string | null = null;
        let contentTypeFromMetadata: string | null = null;
        const apiUrl = AppConfig.api.apiUrl || '';
        try {
          const metadataResponse = await doFetch(`${apiUrl}/api/portal/${VERSION}/repository-files/${fileId}/`);
          if (metadataResponse.ok) {
            const metadata = await metadataResponse.json();
            fallbackFilename = metadata.name || metadata.object_key || null;

            if (
              metadata.type === FileTypes.URL ||
              metadata.type === 'link' ||
              metadata.type === 'url'
            ) {
              linkUrl = metadata.object_key || metadata.url || null;
              contentTypeFromMetadata = 'link';
            }
          }
        } catch (e) {
          // If metadata fetch fails, continue with stream - we'll try to get filename from headers.
        }

        if (contentTypeFromMetadata === 'link') {
          if (!linkUrl) {
            throw new Error('Failed to load link resource.');
          }

          setFileType('link');
          setResourceUrl(linkUrl);
          return;
        }

        const response = await doFetch(`${apiUrl}/api/portal/${VERSION}/repository-files/${fileId}/stream`);

        if (!response.ok) {
          throw new Error('Failed to fetch file.');
        }

        const contentDisposition = response.headers.get('Content-Disposition');
        let filename: string | null = null;

        // Try Content-Disposition header first
        if (contentDisposition) {
          // Try RFC 5987 format: filename*=UTF-8''encoded
          const rfc5987Match = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/);
          if (rfc5987Match && rfc5987Match.length > 1) {
            try {
              filename = decodeURIComponent(rfc5987Match[1]);
            } catch (e) {
              // If decoding fails, try other methods
            }
          }
          
          // Try standard format: filename="value" or filename=value
          if (!filename) {
            const quotedMatch = contentDisposition.match(/filename="(.+?)"/);
            if (quotedMatch && quotedMatch.length > 1) {
              filename = quotedMatch[1];
            } else {
              const unquotedMatch = contentDisposition.match(/filename=([^;]+)/);
              if (unquotedMatch && unquotedMatch.length > 1) {
                filename = unquotedMatch[1].trim().replace(/^["']|["']$/g, '');
              }
            }
          }
        }

        // Use metadata filename as last resort
        if (!filename && fallbackFilename) {
          filename = fallbackFilename;
        }

        if (!filename) {
          throw new Error('Failed to get filename from response headers or metadata.');
        }
        const contentType = filename.split('.').pop();
        if (!contentType) {
          throw new Error('Failed to get content type from filename.');
        }
        setFileType(contentType);

        const blob = await response.blob();
        setBlob(blob);

        if (contentType === 'mp4') {
          const url = URL.createObjectURL(blob);
          setFileUrl(url);
        }
      } catch (err: any) {
        setError(err.message || 'Error loading file.');
      }
    };

    fetchFile();

    // Cleanup function to revoke URL when component unmounts or fileId changes
    return () => {
      // Clean up the object URL to prevent memory leaks
      setFileUrl(prevUrl => {
        if (prevUrl) {
          URL.revokeObjectURL(prevUrl);
        }
        return null;
      });
    };
  }, [fileId, organizationId, guestToken]);

  if (error) {
    return <p className="error">Error: {error}</p>;
  }

  if (
    !fileType ||
    (fileType === 'mp4' && !fileUrl) ||
    (fileType === 'pdf' && !blob) ||
    (fileType === 'link' && !resourceUrl)
  ) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          height: '600px',
        }}
      >
        <Icon name="spinner" loading size="huge" style={{ color: '#2c3e50' }} />
        <div style={{ marginTop: '1em', fontSize: '1.2em', color: '#2c3e50' }}>Loading File...</div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        padding: isFullScreen ? '0' : '4px 16px 12px',
        boxSizing: 'border-box',
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        height: '100%',
      }}
    >
      <div style={{ width: '100%', maxWidth: '100%', minWidth: 0, minHeight: 0, height: '100%', padding: isFullScreen ? '0' : undefined, ...style }}>
        {fileType === 'pdf' ? (
          (() => {
            const PdfViewerComponent = pdfViewerComponent ?? PDFViewer;
            return (
              <PdfViewerComponent
                blob={blob}
                isFullScreen={isFullScreen ?? false}
                viewMode={viewMode}
                scale={scale}
                onViewModeChange={onViewModeChange}
                onScaleChange={onScaleChange}
              />
            );
          })()
        ) : fileType === 'mp4' ? (
          <VideoViewer fileUrl={fileUrl!} isFullScreen={isFullScreen ?? false} />
        ) : fileType === 'link' ? (
          <div style={{ width: '100%', height: '100%', minHeight: 0, minWidth: 0, position: 'relative' }}>
            {resourceUrl ? (
              (() => {
                const youtubeEmbedUrl = getYouTubeEmbedUrl(resourceUrl);
                if (youtubeEmbedUrl) {
                  return (
                    <iframe
                      title="YouTube Preview"
                      src={youtubeEmbedUrl}
                      width="100%"
                      height="100%"
                      style={{ border: 'none', minHeight: '100%', minWidth: '100%' }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  );
                }

                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      height: '100%',
                      padding: '1rem',
                    }}
                  >
                    <p style={{ marginBottom: '1rem' }}>This link cannot be previewed inside the app.</p>
                    <a href={resourceUrl} target="_blank" rel="noopener noreferrer">
                      Open link in a new tab
                    </a>
                  </div>
                );
              })()
            ) : (
              <p>Loading link preview...</p>
            )}
          </div>
        ) : (
          <div>
            <p>Unsupported file type: {fileType}</p>
            <a href={fileUrl ?? undefined} download>
              Download File
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileViewer;

